import { useEffect, useState } from 'react'
import StatusBadge from './StatusBadge'
import SymbolIcon from './SymbolIcon'
import './IngestList.css'

const STATUS_LABELS = { pending: 'In Bearbeitung', completed: 'Archiviert', failed: 'Fehlgeschlagen' }
const STATUS_TONES = { pending: 'info', completed: 'success', failed: 'error' }

export default function IngestList({ apiBase, authHeaders, refreshKey, formatDateTime, tooltipText, canViewAll }) {
  const [result, setResult] = useState(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let stopped = false
    let pollTimer
    let timeoutTimer
    let controller
    let pollAgain = true

    const load = async () => {
      if (document.hidden) return
      controller = new AbortController()
      timeoutTimer = setTimeout(() => controller.abort(), 30000)
      try {
        const response = await fetch(`${apiBase}/web/artifacts/commits`, {
          headers: authHeaders, signal: controller.signal
        })
        if (!response.ok) {
          pollAgain = response.status === 408 || response.status === 429 || response.status >= 500
          throw new Error('Unable to load artifact groups')
        }
        const items = await response.json()
        if (stopped || controller.signal.aborted) return
        setResult({ apiBase, authHeaders, items })
        setError(false)
      } catch {
        if (!stopped) setError(true)
      } finally {
        clearTimeout(timeoutTimer)
        controller = null
        // Keep discovering POS submissions even when all displayed groups are finished.
        if (!stopped && pollAgain && !document.hidden) pollTimer = setTimeout(load, 5000)
      }
    }

    const handleVisibilityChange = () => {
      clearTimeout(pollTimer)
      if (!document.hidden && pollAgain && !controller) void load()
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)
    void load()
    return () => {
      stopped = true
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      clearTimeout(pollTimer)
      clearTimeout(timeoutTimer)
      controller?.abort()
    }
  }, [apiBase, authHeaders, refreshKey])

  // Never display rows from a previous tenant or session.
  const items = result?.apiBase === apiBase && result.authHeaders === authHeaders
    ? result.items : null

  return (
    <section className="card ingest-list">
      <div className="ingest-list-header">
        <div className="card-title">Letzte Archivierungen</div>
        <div className="hint ingest-list-description" data-i18n-live="1">
          {canViewAll
            ? 'Archivierungen aus Web-Uploads und POS-Übermittlungen Ihrer Organisation.'
            : 'Archivierungen aus Ihren Web-Uploads.'}
        </div>
      </div>
      {error && <div className="hint form-error-hint" role="alert">Der Archivierungsstatus konnte nicht aktualisiert werden.</div>}
      <div className="table ingest-table">
        <div className="table-header">
          <span>Erstellt am</span><span>Status</span><span>Dateien</span>
        </div>
        {items?.map((item) => (
          <div className="table-row" key={item.ingest_id} title={tooltipText(item)}>
            <span className="ingest-created-cell">
              <span className="ingest-created-icon" title="Erstellt am">
                <SymbolIcon icon="schedule" />
                <span className="ingest-created-label">Erstellt am</span>
              </span>
              <span className="ingest-mobile-value" data-i18n-skip="1">{formatDateTime(item.created_at)}</span>
            </span>
            <span className="ingest-status-cell">
              <StatusBadge variant="compact" tone={STATUS_TONES[item.status]} title={item.error_code || undefined}>
                {STATUS_LABELS[item.status]}
              </StatusBadge>
            </span>
            <span className="ingest-files-cell">
              <span className="ingest-mobile-label">Dateien</span>
              <span className="ingest-mobile-value" data-i18n-skip="1">{item.artifact_count}</span>
            </span>
          </div>
        ))}
      </div>
      {!items && !error && <div className="hint empty-list-hint" role="status">Lädt…</div>}
      {items?.length === 0 && <div className="hint empty-list-hint">Keine Archivierungen verfügbar</div>}
    </section>
  )
}
